// ============================================================
// DM-LMS — App Router
// Role-based routing: /app/student | /app/tutor | /app/admin
// Auth guard applied at layout level — DRY, not per-page.
// ============================================================

import { Routes, Route, Navigate }  from "react-router-dom";
import { useAuth }                   from "./hooks/useAuth.js";

// Layouts
import AuthLayout    from "./components/layout/AuthLayout.js";
import StudentLayout from "./components/layout/StudentLayout.js";
import TutorLayout   from "./components/layout/TutorLayout.js";
import AdminLayout   from "./components/layout/AdminLayout.js";

// Auth pages
import LoginPage           from "./pages/auth/LoginPage.js";
import CompleteProfilePage from "./pages/auth/CompleteProfilePage.js";

// Student pages
import StudentDashboard   from "./pages/student/DashboardPage.js";
import StudentCourses     from "./pages/student/CoursesPage.js";
import StudentAttendance  from "./pages/student/AttendancePage.js";
import StudentPayments    from "./pages/student/PaymentsPage.js";
import StudentCertificates from "./pages/student/CertificatesPage.js";

// Tutor pages
import TutorDashboard  from "./pages/tutor/DashboardPage.js";
import TutorCourses    from "./pages/tutor/CoursesPage.js";
import TutorAttendance from "./pages/tutor/AttendancePage.js";
import TutorRoster     from "./pages/tutor/RosterPage.js";

// Admin pages
import AdminDashboard    from "./pages/admin/DashboardPage.js";
import AdminUsers        from "./pages/admin/UsersPage.js";
import AdminCourses      from "./pages/admin/CoursesPage.js";
import AdminPayments     from "./pages/admin/PaymentsPage.js";
import AdminCertificates from "./pages/admin/CertificatesPage.js";

// Role guard — DRY: one component, used by every layout
import RequireRole from "./components/auth/RequireRole.js";

export default function App() {
  const { user, isLoading } = useAuth();

  if (isLoading) return <div className="dm-loading">Loading...</div>;

  return (
    <Routes>
      {/* Public auth routes */}
      <Route element={<AuthLayout />}>
        <Route path="/app/login"            element={<LoginPage />} />
        <Route path="/app/complete-profile" element={<CompleteProfilePage />} />
      </Route>

      {/* Student routes */}
      <Route element={<RequireRole role="student"><StudentLayout /></RequireRole>}>
        <Route path="/app/student"              element={<StudentDashboard />} />
        <Route path="/app/student/courses"      element={<StudentCourses />} />
        <Route path="/app/student/attendance"   element={<StudentAttendance />} />
        <Route path="/app/student/payments"     element={<StudentPayments />} />
        <Route path="/app/student/certificates" element={<StudentCertificates />} />
      </Route>

      {/* Tutor routes */}
      <Route element={<RequireRole role="tutor"><TutorLayout /></RequireRole>}>
        <Route path="/app/tutor"             element={<TutorDashboard />} />
        <Route path="/app/tutor/courses"     element={<TutorCourses />} />
        <Route path="/app/tutor/attendance"  element={<TutorAttendance />} />
        <Route path="/app/tutor/roster"      element={<TutorRoster />} />
      </Route>

      {/* Admin routes */}
      <Route element={<RequireRole role="admin"><AdminLayout /></RequireRole>}>
        <Route path="/app/admin"                element={<AdminDashboard />} />
        <Route path="/app/admin/users"          element={<AdminUsers />} />
        <Route path="/app/admin/courses"        element={<AdminCourses />} />
        <Route path="/app/admin/payments"       element={<AdminPayments />} />
        <Route path="/app/admin/certificates"   element={<AdminCertificates />} />
      </Route>

      {/* Smart root redirect — after Google OAuth callback, send incomplete
          profiles to complete-profile, otherwise straight to the role dashboard. */}
      <Route
        path="/app"
        element={
          !user ? (
            <Navigate to="/app/login" replace />
          ) : user.phone === null ? (
            <Navigate to="/app/complete-profile" replace />
          ) : (
            <Navigate to={`/app/${user.role}`} replace />
          )
        }
      />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/app" replace />} />
    </Routes>
  );
}
